import { Fragment, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { ADMIN_MENU, ExternalIcon, type MenuEntry } from './adminMenu';
import { UserMenu } from './UserMenu';
import { BrandLogo } from '../components/BrandLogo';
import { BRAND } from '../brand';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;
const SIDEBAR_KEY = 'dtsadmin:sidebar-collapsed';

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function isPathActive(currentPath: string, target: string): boolean {
  if (target === '/') return currentPath === '/';
  return currentPath === target || currentPath.startsWith(`${target}/`);
}

function findInitiallyOpenGroups(currentPath: string): Set<string> {
  const open = new Set<string>();
  for (const entry of ADMIN_MENU) {
    if (entry.kind !== 'group') continue;
    const hasActiveChild = entry.children.some(
      (c) => c.path !== undefined && isPathActive(currentPath, c.path),
    );
    if (hasActiveChild) open.add(entry.labelKey);
  }
  return open;
}

export function AdminLayout(): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(readSidebarCollapsed);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() =>
    findInitiallyOpenGroups(location.pathname),
  );

  const closeDrawer = (): void => setMobileOpen(false);

  function toggleSidebar(): void {
    setDesktopCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* navegador sem storage */
      }
      return next;
    });
  }

  function expandSidebar(): void {
    setDesktopCollapsed(false);
    try {
      localStorage.setItem(SIDEBAR_KEY, '0');
    } catch {
      /* navegador sem storage */
    }
  }

  function toggleGroup(key: string): void {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const drawerWidth = desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  function renderDrawer(collapsed: boolean): JSX.Element {
    return (
      <Box role="navigation" aria-label={t('layout.sideMenu')}>
        <Toolbar
          sx={{
            px: collapsed ? 1 : 1.5,
            gap: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <IconButton
            onClick={toggleSidebar}
            aria-label={collapsed ? t('menu.showMenu') : t('menu.hideMenu')}
            aria-expanded={!collapsed}
          >
            <MenuIcon />
          </IconButton>
          {!collapsed && <BrandLogo src={BRAND.logo} height={32} fallbackColor="primary.main" />}
        </Toolbar>
        <List component="nav">
          {ADMIN_MENU.map((entry) => (
            <MenuEntryView
              key={entry.labelKey}
              entry={entry}
              currentPath={location.pathname}
              openGroups={openGroups}
              toggleGroup={toggleGroup}
              onNavigate={closeDrawer}
              collapsed={collapsed}
              onExpandMenu={expandSidebar}
            />
          ))}
        </List>
      </Box>
    );
  }

  const drawerContent = renderDrawer(false);
  const desktopDrawer = renderDrawer(desktopCollapsed);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar>
          {!isDesktop && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen((v) => !v)}
              sx={{ mr: 2 }}
              aria-label={t('menu.openMenu')}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* A logo do admin fica no topo do menu lateral (não repete na AppBar). */}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('layout.adminGreeting')}
          </Typography>
          <Typography variant="caption" component="span" sx={{ mr: 1, opacity: 0.85 }}>
            {t('app.version', { version: __APP_VERSION__ })}
          </Typography>
          <UserMenu />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
        >
          {desktopDrawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

interface MenuEntryViewProps {
  entry: MenuEntry;
  currentPath: string;
  openGroups: Set<string>;
  toggleGroup: (key: string) => void;
  onNavigate: () => void;
  collapsed: boolean;
  onExpandMenu: () => void;
}

function MenuEntryView({
  entry,
  currentPath,
  openGroups,
  toggleGroup,
  onNavigate,
  collapsed,
  onExpandMenu,
}: MenuEntryViewProps): JSX.Element {
  const { t } = useTranslation();
  const itemSx = collapsed ? { px: 1.5, justifyContent: 'center' } : undefined;
  const iconSx = collapsed ? { minWidth: 0, justifyContent: 'center' } : undefined;

  if (entry.kind === 'leaf') {
    const Icon = entry.icon;
    const label = t(entry.labelKey);
    if (entry.externalUrl) {
      const button = (
        <ListItemButton
          component="a"
          href={entry.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${label} — ${t('common.openExternal')}`}
          sx={itemSx}
        >
          <ListItemIcon sx={iconSx}>
            <Icon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary={label} />}
          {!collapsed && <ExternalIcon fontSize="small" sx={{ opacity: 0.6 }} />}
        </ListItemButton>
      );
      return collapsed ? <Tooltip title={label}>{button}</Tooltip> : button;
    }
    const active = entry.path !== undefined && isPathActive(currentPath, entry.path);
    const button = (
      <ListItemButton
        component={RouterLink}
        to={entry.path ?? '/'}
        selected={active}
        onClick={onNavigate}
        aria-label={collapsed ? label : undefined}
        sx={itemSx}
      >
        <ListItemIcon sx={iconSx}>
          <Icon />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={label} />}
      </ListItemButton>
    );
    return collapsed ? <Tooltip title={label}>{button}</Tooltip> : button;
  }

  // group
  const Icon = entry.icon;
  const isOpen = openGroups.has(entry.labelKey);
  const hasActive = entry.children.some(
    (c) => c.path !== undefined && isPathActive(currentPath, c.path),
  );

  return (
    <Fragment>
      <Tooltip title={collapsed ? t(entry.labelKey) : ''} placement="right">
        <ListItemButton
          onClick={() => {
            if (collapsed) {
              onExpandMenu();
              if (!isOpen) toggleGroup(entry.labelKey);
              return;
            }
            toggleGroup(entry.labelKey);
          }}
          aria-expanded={collapsed ? false : isOpen}
          aria-controls={`menu-group-${entry.labelKey}`}
          aria-label={
            collapsed
              ? t(entry.labelKey)
              : isOpen
                ? t('menu.collapseGroup', { label: t(entry.labelKey) })
                : t('menu.expandGroup', { label: t(entry.labelKey) })
          }
          selected={hasActive && !isOpen}
          sx={itemSx}
        >
          <ListItemIcon sx={iconSx}>
            <Icon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary={t(entry.labelKey)} />}
          {!collapsed && (isOpen ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </Tooltip>
      <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit id={`menu-group-${entry.labelKey}`}>
        <List component="div" disablePadding>
          {entry.children.map((child) => {
            if (child.externalUrl) {
              return (
                <ListItemButton
                  key={child.labelKey}
                  component="a"
                  href={child.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ pl: 7 }}
                  aria-label={`${t(child.labelKey)} — ${t('common.openExternal')}`}
                >
                  <ListItemText primary={t(child.labelKey)} />
                  <ExternalIcon fontSize="small" sx={{ opacity: 0.6 }} />
                </ListItemButton>
              );
            }
            const childActive = child.path !== undefined && isPathActive(currentPath, child.path);
            return (
              <ListItemButton
                key={child.labelKey}
                component={RouterLink}
                to={child.path ?? '/'}
                selected={childActive}
                onClick={onNavigate}
                sx={{ pl: 7 }}
              >
                <ListItemText primary={t(child.labelKey)} />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </Fragment>
  );
}
